import { useCallback, useEffect, useRef, useState } from "react";
import GetBrandsService from "@/http/api/brand/GetBrandsServices";
import useDebounce from "@/hook/useDebounce";
import { FilterBrandProps } from "@/interface/components/brands";

const { getBrands } = new GetBrandsService();

export const useBrandFilter = () => {
    const [brands, setBrands] = useState<FilterBrandProps[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(true);

    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const debouncedSelectedBrands = useDebounce(selectedBrands, 500);

    // Prevent double execution
    const isFirstRender = useRef(true);

    const fetchBrands = useCallback(async (currentPage: number, isLoadMore: boolean = false) => {
        setLoading(true);
        try {
            const { getBrands } = new GetBrandsService();
            const data: any = await getBrands({ params: `page=${currentPage}` });
            const newBrands: any = data?.data?.data || [];

            if (newBrands.length === 0) {
                setHasMore(false);
            } else {
                setBrands((prevBrands) => (isLoadMore ? [...prevBrands, ...newBrands] : newBrands));
                setPage(currentPage);
            }
        } catch (error) {
            console.error("Error fetching brands:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchBrandsBySearch = useCallback(async (query: string) => {
        setLoading(true);
        try {
            const data: any = await getBrands({ params: `search=${query}` });
            const filteredBrands: any = data?.data?.data || [];
            setBrands(filteredBrands);
            setHasMore(false);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (!debouncedSearchTerm) {
            setPage(1);
            fetchBrands(1);
        } else {
            fetchBrandsBySearch(debouncedSearchTerm);
        }
    }, [debouncedSearchTerm, fetchBrands, fetchBrandsBySearch]);

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setPage(1);
        setHasMore(true);
    };

    const handleBrandSelect = useCallback((brandId: number) => {
        setSelectedBrands((prevSelectedBrands) => {
            const brandIdString = String(brandId);
            if (prevSelectedBrands.includes(brandIdString)) {
                return prevSelectedBrands.filter((id) => id !== brandIdString);
            } else {
                return [...prevSelectedBrands, brandIdString];
            }
        });
    }, []);

    // API call triggered by debounced selectedBrands
    useEffect(() => {
        const updateBrandsQuery = async () => {
            if (debouncedSelectedBrands.length > 0) {
                setLoading(true);
                try {
                    const { getBrands } = new GetBrandsService();
                    await getBrands({
                        params: `brands[]=${debouncedSelectedBrands.join(",")}`,
                    });
                } catch (error) {
                    console.error("Error updating brands:", error);
                } finally {
                    setLoading(false);
                }
            }
        };

        updateBrandsQuery();
    }, [debouncedSelectedBrands]);

    const loadMore = useCallback(() => {
        if (hasMore && !loading && !debouncedSearchTerm) {
            fetchBrands(page + 1, true);
        }
    }, [hasMore, loading, debouncedSearchTerm, page, fetchBrands]);

    return {
        brands,
        searchTerm,
        selectedBrands,
        loading,
        hasMore,
        handleSearchChange,
        handleBrandSelect,
        loadMore,
    };
};
